<template>
  <div class="p2p-page">
    <div v-if="error" class="p2p-alert bad">{{ error }}</div>
    <div v-if="!project" class="p2p-skeleton" style="height:160px"></div>
    <template v-else>
      <div class="p2p-between">
        <div class="p2p-row" style="flex-wrap:nowrap;gap:14px;min-width:0">
          <MediaImg v-if="project.product && project.product.image_url" :src="project.product.image_url" ratio="1" class="p2p-project-logo" />
          <div style="min-width:0">
            <h1 class="p2p-h1" style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ project.name }}</h1>
            <div class="p2p-faint p2p-mono">{{ host }}</div>
          </div>
        </div>
        <div class="p2p-row">
          <router-link :to="'/create/' + project.id + '/formats'" class="p2p-btn"><v-icon small color="#0b0b10">mdi-plus</v-icon> Create posts</router-link>
          <v-menu offset-y left>
            <template #activator="{ on, attrs }"><button type="button" class="p2p-icon-btn" aria-label="Project actions" v-bind="attrs" v-on="on"><v-icon>mdi-dots-vertical</v-icon></button></template>
            <v-list dense>
              <v-list-item :to="'/create/' + project.id + '/source'"><v-list-item-title>Change sources or read again</v-list-item-title></v-list-item>
              <v-list-item :disabled="busy" @click="duplicate"><v-list-item-title>Duplicate project</v-list-item-title></v-list-item>
              <v-list-item @click="confirmDelete = true"><v-list-item-title class="p2p-bad">Delete project</v-list-item-title></v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <div class="p2p-tabs" role="tablist">
        <button v-for="item in tabs" :key="item.key" type="button" role="tab" :aria-selected="tab === item.key ? 'true' : 'false'" :class="{ on: tab === item.key }" @click="setTab(item.key)">{{ item.label }}</button>
      </div>

      <!-- Posts -->
      <section v-if="tab === 'posts'" class="p2p-stack">
        <div v-if="!posts.length && !postsLoading" class="p2p-empty">
          <strong>No posts yet</strong>
          <router-link :to="'/create/' + project.id + '/formats'" class="p2p-btn">Create posts</router-link>
        </div>
        <template v-for="group in grouped">
          <div :key="'h' + group.batch" class="p2p-between">
            <h2 class="p2p-h2">{{ group.label }}</h2>
            <router-link :to="'/create/' + project.id + '/review?batch=' + group.batch" class="p2p-btn sm outline">Review and publish</router-link>
          </div>
          <PostGrid :key="'g' + group.batch" v-slot="{ cell }" :count="group.posts.length">
            <PostCard v-for="(post, index) in group.posts" :key="post.id" :post="post" :images="images" :platforms="platforms" :prices="prices"
                      :wide="cell(index).wide" :style="cell(index).style"
                      @updated="replacePost" @operation="loadPosts" @delete="deletePost" />
          </PostGrid>
        </template>
      </section>

      <!-- Product -->
      <section v-if="tab === 'product'" class="p2p-stack">
        <div v-if="!project.knowledge_ready" class="p2p-empty">
          <strong>We have not read this product yet</strong>
          <router-link :to="'/create/' + project.id + '/source'" class="p2p-btn">Read the site</router-link>
        </div>
        <StepFormats v-else :project="project" :config="config" :workspace="workspace" knowledge-only @project="project = $event" @go="go" @refresh="load" />
        <div v-if="project.memory && (project.memory.voice || project.memory.rejected.length)" class="p2p-card">
          <h3 class="p2p-h3">What the writer learned</h3>
          <p v-if="project.memory.voice" class="p2p-text" style="margin:0">Your wording in {{ project.memory.voice }} edited post{{ project.memory.voice === 1 ? '' : 's' }}: new posts follow your voice.</p>
          <div v-if="project.memory.rejected.length" class="p2p-stack" style="gap:4px">
            <span class="p2p-text">Posts you rejected, which new posts avoid:</span>
            <span v-for="(item, i) in project.memory.rejected" :key="i" class="p2p-faint">“{{ item.hook }}” <span v-if="item.reason && item.reason !== 'rejected'">({{ item.reason.replace(/_/g, ' ') }})</span></span>
          </div>
          <button type="button" class="p2p-btn sm ghost" style="align-self:flex-start" :disabled="busy" @click="forget">Forget this</button>
        </div>
      </section>

      <!-- Autopilot -->
      <section v-if="tab === 'automation'" class="p2p-stack">
        <div v-if="!project.automation" class="p2p-empty">
          <strong>Autopilot is off</strong>
          <span>Fresh posts from your site on the days you choose, approved by you or published automatically.</span>
          <router-link :to="'/create/' + project.id + '/publish?tab=automate'" class="p2p-btn">Set up autopilot</router-link>
        </div>
        <template v-else>
          <div class="p2p-card">
            <div class="p2p-between">
              <div class="p2p-row"><span :class="['p2p-dot', automation.status === 'active' ? 'is-accent' : 'warn']"></span>
                <h2 class="p2p-h2">{{ automation.status === 'active' ? 'Autopilot is on' : 'Autopilot is paused' }}</h2></div>
              <div class="p2p-row">
                <button type="button" class="p2p-btn sm soft" :disabled="busy" @click="setStatus(automation.status === 'active' ? 'paused' : 'active')">{{ automation.status === 'active' ? 'Pause' : 'Resume' }}</button>
                <router-link :to="'/create/' + project.id + '/publish?tab=automate'" class="p2p-btn sm outline">Edit</router-link>
              </div>
            </div>
            <p class="p2p-text">{{ scheduleText }} · {{ automation.approval_mode === 'review' ? 'you approve each post' : 'publishes automatically after checks' }}</p>
            <p class="p2p-faint" style="margin:0">{{ optionsText }}</p>
            <p v-if="usage" class="p2p-faint" style="margin:0">This month {{ usage.this_month }} credit{{ Number(usage.this_month) === 1 ? '' : 's' }} used · about {{ usage.estimate_month }} a month at this pace<span v-if="Number(usage.monthly_cap)"> · limit {{ usage.monthly_cap }}</span><span v-if="runs.length"> · last run: {{ runLabel(runs[0].status).toLowerCase() }}</span></p>
            <div v-if="automation.status !== 'active' && automation.pause_reason" class="p2p-alert">{{ pauseText }}</div>
            <div v-if="automation.upcoming.length" class="p2p-stack" style="gap:6px">
              <span class="p2p-kicker">Next posts</span>
              <div class="p2p-row" style="gap:8px"><span v-for="slot in automation.upcoming" :key="slot" class="p2p-chip mono">{{ when(slot) }}</span></div>
            </div>
          </div>
          <div class="p2p-card">
            <h3 class="p2p-h3">Recent runs</h3>
            <div v-if="!runs.length" class="p2p-muted">No runs yet. The first post is written a day before its slot.</div>
            <table v-else class="p2p-table">
              <thead><tr><th>Slot</th><th>Format</th><th>Status</th></tr></thead>
              <tbody><tr v-for="run in runs" :key="run.id"><td>{{ when(run.slot_at) }}</td><td>{{ run.format }}</td>
                <td><span :class="['p2p-chip', runTone(run.status)]">{{ runLabel(run.status) }}</span> <span v-if="run.error" class="p2p-faint">{{ run.error }}</span></td></tr></tbody>
            </table>
          </div>
        </template>
      </section>

      <!-- Results -->
      <section v-if="tab === 'results'" class="p2p-stack">
        <div v-if="!analytics" class="p2p-skeleton" style="height:180px"></div>
        <div v-else-if="!analytics.published" class="p2p-empty">
          <strong>No published posts yet</strong><span>Numbers appear here a little while after your posts go out.</span>
          <router-link :to="'/create/' + project.id + '/formats'" class="p2p-btn">Create posts</router-link>
        </div>
        <template v-else>
          <div class="p2p-grid p2p-stats">
            <div v-for="item in totals" :key="item.key" class="p2p-card tight"><span class="p2p-kicker">{{ item.label }}</span>
              <span class="p2p-stat">{{ item.value === null ? '—' : item.value }}</span></div>
          </div>
          <p class="p2p-faint" style="margin:0">{{ analytics.measured }} of {{ analytics.published }} published posts have numbers<span v-if="analytics.waiting">; {{ analytics.waiting }} still waiting for the networks</span><span v-if="analytics.updated_at"> · updated {{ when(analytics.updated_at) }}</span>.</p>
          <div v-if="analytics.best_format" class="p2p-alert is-info">{{ formatName(analytics.best_format) }} posts get the most interactions in this project so far.</div>
          <div class="p2p-card">
            <h3 class="p2p-h3">By format</h3>
            <table class="p2p-table"><thead><tr><th>Format</th><th>Posts</th><th>Views</th><th>Interactions</th><th>Per post</th></tr></thead>
              <tbody><tr v-for="row in analytics.by_format" :key="row.key"><td>{{ formatName(row.key) }}</td><td>{{ row.posts }}</td>
                <td>{{ show(row.views) }}</td><td>{{ show(row.engagement) }}</td><td>{{ show(row.average_engagement) }}</td></tr></tbody></table>
          </div>
          <div class="p2p-card">
            <h3 class="p2p-h3">By network</h3>
            <table class="p2p-table"><thead><tr><th>Network</th><th>Posts</th><th>Views</th><th>Interactions</th></tr></thead>
              <tbody><tr v-for="row in analytics.by_platform" :key="row.key"><td>{{ networkName(row.key) }}</td><td>{{ row.measured }}</td>
                <td>{{ show(row.views) }}</td><td>{{ show(row.engagement) }}</td></tr></tbody></table>
          </div>
          <div class="p2p-card">
            <h3 class="p2p-h3">Best posts</h3>
            <div v-for="item in analytics.top" :key="item.delivery_id" class="p2p-between" style="gap:12px">
              <span style="min-width:0"><strong>{{ item.hook }}</strong><br><span class="p2p-faint">{{ formatName(item.format) }} · {{ networkName(item.platform) }}</span></span>
              <span class="p2p-row" style="gap:8px;flex-wrap:nowrap"><span class="p2p-chip mono">{{ show(item.metrics.engagement) }} interactions</span>
                <a v-if="item.public_url" :href="item.public_url" target="_blank" rel="noopener" class="p2p-btn sm soft">View</a></span>
            </div>
          </div>
          <ul v-if="analytics.notes.length" class="p2p-facts p2p-faint"><li v-for="note in analytics.notes" :key="note">{{ note }}</li></ul>
        </template>
      </section>

      <!-- Activity -->
      <section v-if="tab === 'activity'" class="p2p-stack">
        <p class="p2p-muted" style="margin:0">Everything that happened in this project: writing, edits, approvals, scheduling and publishing.</p>
        <div v-if="activityLoading && !activity.length" class="p2p-skeleton" style="height:160px"></div>
        <div v-else-if="!activity.length" class="p2p-empty"><strong>Nothing yet</strong><span>Actions in this project appear here.</span></div>
        <ol v-else class="p2p-activity">
          <li v-for="item in activity" :key="item.id">
            <span class="p2p-activity-dot" aria-hidden="true"></span>
            <div class="p2p-stack" style="gap:2px;min-width:0">
              <span class="p2p-text">{{ describe(item) }}</span>
              <span class="p2p-faint p2p-mono">{{ when(item.created_at) }}</span>
            </div>
          </li>
        </ol>
        <button v-if="activityMore" type="button" class="p2p-btn sm ghost" style="align-self:flex-start" :disabled="activityLoading" @click="loadActivity(true)">Show older</button>
      </section>

      <!-- Sources -->
      <section v-if="tab === 'sources'" class="p2p-stack">
        <div class="p2p-card">
          <div class="p2p-between"><h3 class="p2p-h3">Website</h3><router-link :to="'/create/' + project.id + '/source'" class="p2p-btn sm outline">Change or read again</router-link></div>
          <span class="p2p-mono p2p-muted">{{ project.primary_url || 'No website' }}</span>
          <span v-if="project.knowledge && project.knowledge.pages" class="p2p-faint">Pages read: {{ project.knowledge.pages.join(', ') }}</span>
        </div>
        <div class="p2p-grid" style="grid-template-columns:repeat(auto-fill,minmax(160px,1fr))">
          <div v-for="source in uploads" :key="source.id" class="p2p-card tight">
            <MediaImg v-if="source.media" :src="source.media.poster_url || source.media.url" ratio="4 / 3" />
            <span class="p2p-faint" style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ source.filename }}</span>
            <span v-if="source.speech && source.speech.text" class="p2p-faint p2p-clamp3" :title="source.speech.text">Says: “{{ source.speech.text }}”</span>
            <span v-if="seenIn(source)" class="p2p-faint p2p-clamp3" :title="seenIn(source)">We see: {{ seenIn(source) }}</span>
          </div>
        </div>
      </section>

      <v-dialog v-model="confirmDelete" max-width="460">
        <div class="p2p-card raised">
          <h2 class="p2p-h2">Delete {{ project.name }}?</h2>
          <p class="p2p-text">Its posts and autopilot are removed and scheduled posts are cancelled. Posts already published stay on the networks. Its files are permanently deleted after seven days.</p>
          <div class="p2p-row">
            <button type="button" class="p2p-btn danger" :disabled="busy" @click="deleteProject">Delete project</button>
            <button type="button" class="p2p-btn ghost" @click="confirmDelete = false">Keep it</button>
          </div>
        </div>
      </v-dialog>
    </template>
  </div>
</template>

<script>
import MediaImg from '../components/MediaImg.vue';
import PostCard from '../components/PostCard.vue';
import PostGrid from '../components/PostGrid.vue';
import StepFormats from '../components/steps/StepFormats.vue';
import { LABELS } from '../components/NetBadge.vue';
import { get, post, remove, message } from '../api';
import { localDate, hostOf, poller } from '../poll';

const DAY_NAMES = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };

export default {
  name: 'ProjectDetail',
  components: { MediaImg, PostCard, PostGrid, StepFormats },
  data: () => ({ project: null, posts: [], postsLoading: false, runs: [], usage: null, error: '', busy: false, confirmDelete: false, loop: null,
    activity: [], activityMore: false, activityLoading: false,
    analytics: null,
    tabs: [{ key: 'posts', label: 'Posts' }, { key: 'results', label: 'Results' }, { key: 'product', label: 'Product' },
      { key: 'automation', label: 'Autopilot' }, { key: 'sources', label: 'Sources' }, { key: 'activity', label: 'Activity' }] }),
  computed: {
    tab() { return this.$route.query.tab || 'posts'; },
    config() { return this.$store.getters['publisher/config']; },
    workspace() { return this.$store.getters['publisher/workspace']; },
    prices() { return this.config ? this.config.prices : undefined; },
    host() { return hostOf(this.project && this.project.primary_url); },
    automation() { return this.project && this.project.automation; },
    uploads() { return (this.project.sources || []).filter(source => source.kind === 'image' || source.kind === 'video'); },
    images() {
      const knowledge = this.project && this.project.knowledge;
      return knowledge ? knowledge.images.map(image => ({ id: image.id, url: image.url, alt: image.alt || 'From your site', seen: image.seen || '' })) : [];
    },
    platforms() {
      const seen = [];
      this.$store.getters['publisher/connected'].forEach(account => { if (seen.indexOf(account.platform) < 0) seen.push(account.platform); });
      return seen;
    },
    grouped() {
      const groups = [];
      this.posts.forEach(item => {
        let group = groups.find(entry => entry.batch === item.batch_id);
        if (!group) {
          const batch = (this.project.batches || []).find(entry => entry.id === item.batch_id);
          const label = (item.run_id ? 'Autopilot post' : batch && batch.kind === 'sample' ? 'Sample' : 'Batch') + ' · ' + localDate(batch ? batch.created_at : item.created_at, { month: 'short', day: 'numeric' });
          group = { batch: item.batch_id, label, posts: [] };
          groups.push(group);
        }
        group.posts.push(item);
      });
      return groups;
    },
    scheduleText() {
      const a = this.automation;
      return a.days.map(day => DAY_NAMES[day]).join(', ') + ' at ' + a.time_local + ' (' + a.timezone + ') · ' + a.formats.join(', ');
    },
    optionsText() {
      const s = this.automation.settings || {};
      const parts = [(s.posts_per_run || 1) + ' post' + ((s.posts_per_run || 1) === 1 ? '' : 's') + ' each time'];
      parts.push({ never: 'never re-reads your site', weekly: 're-reads your site weekly', each_run: 're-reads your site before every post' }[s.refresh || 'weekly']);
      if ((s.approval_hours || 1) > 1) parts.push('approvals close ' + s.approval_hours + ' hours before');
      if (s.monthly_cap) parts.push('at most ' + s.monthly_cap + ' credits a month');
      if (s.tone) parts.push(s.tone + ' voice');
      if ((s.quiet_periods || []).length) parts.push('quiet: ' + s.quiet_periods.map(p => p.start + ' to ' + p.end).join(', '));
      return parts.join(' · ');
    },
    totals() {
      const t = this.analytics.totals;
      return [{ key: 'views', label: 'Views', value: t.views }, { key: 'reactions', label: 'Reactions', value: t.reactions },
        { key: 'comments', label: 'Comments', value: t.comments }, { key: 'shares', label: 'Shares', value: t.shares },
        { key: 'clicks', label: 'Clicks', value: t.clicks }];
    },
    pauseText() {
      const reasons = { credits: 'Paused because your credits ran out. Add credits, then resume.',
        plan: 'Paused because your plan ended. Choose a plan, then resume.', error: 'Paused after three runs in a row failed. Resume to try again.',
        publish: 'Paused because its last three posts could not be published. Check your connections, then resume.',
        paused_by_you: 'You paused it.', paused_all: 'Paused with all your autopilots. Resume it here or from Projects.',
        paused_by_support: 'Paused by our support team. Contact us if you have questions.', project_unavailable: 'Paused because the project is unavailable.' };
      return reasons[this.automation.pause_reason] || 'Paused.';
    }
  },
  watch: { '$route.params.id': { immediate: true, handler() { this.load(); } }, tab() { this.loadTab(); } },
  beforeDestroy() { if (this.loop) this.loop.stop(); },
  methods: {
    when(value) { return localDate(value); },
    // What the last reading's image look saw in an uploaded picture, if it looked.
    seenIn(source) {
      const knowledge = this.project && this.project.knowledge;
      const seen = knowledge && knowledge.uploads_seen;
      return seen && seen[source.ref] ? seen[source.ref] : '';
    },
    setTab(key) { this.$router.replace({ query: { tab: key } }).catch(() => {}); },
    go(step, query) { this.$router.push({ path: '/create/' + this.project.id + '/' + step, query: query || {} }).catch(() => {}); },
    async load() {
      try {
        this.project = await get('/projects/' + this.$route.params.id);
        this.$store.dispatch('publisher/loadConfig');
        this.$store.dispatch('publisher/loadAccounts').catch(() => {});
        this.loadTab();
      } catch (error) {
        // Another member's project answers 404 exactly like a deleted one: nothing about it is revealed.
        this.error = error && error.response && error.response.status === 404
          ? 'We could not find this project. It may have been deleted, or it belongs to another account.' : message(error);
      }
    },
    loadTab() {
      if (this.tab === 'posts') this.loadPosts();
      if (this.tab === 'automation' && this.automation) this.loadRuns();
      if (this.tab === 'activity') this.loadActivity(false);
      if (this.tab === 'results') this.loadAnalytics();
    },
    async loadAnalytics() {
      try { this.analytics = await get('/projects/' + this.project.id + '/analytics'); } catch (error) { this.error = message(error); }
    },
    show(value) { return value === null || value === undefined ? '—' : value; },
    formatName(key) { return { text: 'Text', image: 'Image', carousel: 'Carousel', video: 'Video' }[key] || key; },
    networkName(key) { return LABELS[key] || key; },
    async forget() {
      this.busy = true;
      try { this.project = await remove('/projects/' + this.project.id + '/memory'); } catch (error) { this.error = message(error); } finally { this.busy = false; }
    },
    async loadActivity(older) {
      this.activityLoading = true;
      try {
        const before = older && this.activity.length ? this.activity[this.activity.length - 1].id : undefined;
        const data = await get('/projects/' + this.project.id + '/activity', before ? { before } : undefined);
        this.activity = older ? this.activity.concat(data.events) : data.events;
        this.activityMore = data.more;
      } catch (error) {
        this.error = message(error);
      } finally {
        this.activityLoading = false;
      }
    },
    describe(item) {
      const d = item.details || {};
      const net = d.platform ? ' on ' + (LABELS[d.platform] || d.platform) : '';
      const plural = (n, word) => n + ' ' + word + (n === 1 ? '' : 's');
      const words = value => String(value || '').replace(/_/g, ' ');
      const text = {
        project_created: 'Project created', project_duplicated: 'Project created as a copy',
        sample_requested: 'Sample requested', ingest_requested: 'Asked to read the site' + (d.has_url ? '' : ' from your notes') + (d.videos ? ' and ' + plural(d.videos, 'video') : ''),
        upload_added: 'Uploaded ' + (d.media === 'video' ? 'a video' : 'an image'),
        transcribe_requested: 'Read what a video says', memory_cleared: 'Cleared what the writer learned',
        source_refreshed: (d.run_id ? 'Site re-read before an autopilot post: ' : 'Site read again: ') + ((d.added || d.removed) ? plural(d.added || 0, 'new fact') + ', ' + plural(d.removed || 0, 'fact') + ' no longer found' + (d.new_facts && d.new_facts.length ? ' (' + d.new_facts.join('; ') + ')' : '') : 'nothing changed'),
        transcript_edited: d.action === 'cleared' ? 'Stopped using what a video says' : d.action === 'reset' ? 'Went back to the words heard in a video' : 'Edited what a video says',
        knowledge_edited: 'Product details edited' + (d.fields ? ' (' + words(d.fields.join(', ')) + ')' : ''),
        never_say_updated: 'Never-say list updated',
        batch_requested: 'Asked for ' + plural(d.posts || 0, 'post') + (d.credits ? ' · ' + d.credits + ' credits' : ''),
        rewrite_requested: 'Rewrite requested (' + words(d.instruction) + ')', visual_requested: 'New visual requested',
        render_requested: 'Visual redrawn with your words',
        post_edited: 'Post edited' + (d.fields && d.fields.length ? ' (' + d.fields.join(', ') + ')' : ''),
        published: 'Published ' + plural(d.posts || 0, 'post') + ' to ' + plural(d.deliveries || 0, 'account'),
        scheduled: 'Scheduled ' + plural(d.posts || 0, 'post') + ' for ' + plural(d.deliveries || 0, 'account'),
        delivery_retried: 'Retried a failed post' + net, delivery_cancelled: 'Cancelled a scheduled post' + net,
        delivery_rescheduled: 'Moved a scheduled post' + net,
        approval_approved: 'Approved an autopilot post' + (d.by_reviewer ? ' (by your reviewer)' : ''),
        approval_rejected: 'Rejected an autopilot post' + (d.reason ? ' (' + words(d.reason) + ')' : '') + (d.by_reviewer ? ' (by your reviewer)' : ''),
        automation_created: 'Autopilot turned on', automation_updated: 'Autopilot settings changed',
        automation_paused: 'Autopilot paused', automation_resumed: 'Autopilot resumed',
        support_action: 'Support: ' + words(d.action || 'update') + (d.outcome ? ' (' + words(d.outcome) + ')' : '')
      }[item.type];
      return text || words(item.type);
    },
    async duplicate() {
      this.busy = true;
      try {
        const copy = await post('/projects/' + this.project.id + '/duplicate');
        this.$router.push('/projects/' + copy.id).catch(() => {});
      } catch (error) {
        this.error = message(error);
      } finally {
        this.busy = false;
      }
    },
    async loadPosts() {
      if (this.loop) this.loop.stop();
      this.loop = poller(async () => {
        this.postsLoading = true;
        try {
          this.posts = (await get('/projects/' + this.project.id + '/posts', { limit: 120 })).posts;
        } finally {
          this.postsLoading = false;
        }
        return this.posts.some(item => item.visual_status === 'processing' || item.visual_status === 'pending');
      }, 3000);
      this.loop.start();
    },
    async loadRuns() {
      try {
        const data = await get('/automations/' + this.automation.id + '/runs');
        this.runs = data.runs;
        this.usage = data.usage || null;
      } catch (error) { this.error = message(error); }
    },
    replacePost(updated) {
      const index = this.posts.findIndex(item => item.id === updated.id);
      if (index >= 0) this.$set(this.posts, index, updated);
    },
    async deletePost(item) {
      try { await remove('/posts/' + item.id); this.posts = this.posts.filter(entry => entry.id !== item.id); } catch (error) { this.error = message(error); }
    },
    async setStatus(status) {
      this.busy = true;
      try {
        const updated = await post('/automations/' + this.automation.id + '/status', { status });
        this.project = { ...this.project, automation: updated };
      } catch (error) {
        this.error = message(error);
      } finally {
        this.busy = false;
      }
    },
    runLabel(status) {
      return { created: 'Starting', refreshing: 'Re-reading your site', drafting: 'Writing', visuals: 'Designing', ready: 'Ready', awaiting_approval: 'Waiting for approval',
        scheduled: 'Scheduled', skipped: 'Skipped', rejected: 'Rejected', failed: 'Failed', needs_credits: 'Needs credits' }[status] || status;
    },
    runTone(status) { return { scheduled: 'good', failed: 'bad', needs_credits: 'warn', skipped: 'warn', awaiting_approval: 'is-accent' }[status] || ''; },
    async deleteProject() {
      this.busy = true;
      try {
        await remove('/projects/' + this.project.id);
        this.$router.push('/').catch(() => {});
      } catch (error) {
        this.error = message(error);
        this.confirmDelete = false;
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>
